import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Progress, Modal } from 'antd';
import message from '../../utils/message';
import { LeftOutlined, RightOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import styles from './SATExaminationContent.module.scss';

const SATExaminationContent = ({ examinationId, onComplete, onExit }) => {
  // Main state
  const [examination, setExamination] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [userAnswers, setUserAnswers] = useState(new Map());
  const [loading, setLoading] = useState(false);

  // Question cache for performance
  const [questionCache, setQuestionCache] = useState(new Map());

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState({
    minutes: 0,
    seconds: 0,
    percentage: 100,
    totalMs: 0
  });
  const [timeWarning, setTimeWarning] = useState(false);
  const [timeCritical, setTimeCritical] = useState(false);

  // Section info
  const [sectionInfo, setSectionInfo] = useState({
    current: null,
    questionNumberInSection: 0,
    allSections: []
  });

  // Refs for intervals
  const timerIntervalRef = useRef(null);
  const preloadTimeoutRef = useRef(null);

  // API Functions
  const fetchQuestion = useCallback(async (questionIndex, useCache = true) => {
    // Check cache first
    if (useCache && questionCache.has(questionIndex)) {
      return questionCache.get(questionIndex);
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token'); // Adjust based on your auth system

      const response = await fetch(
        `/examinations/${examinationId}/questions?questionIndex=${questionIndex}&preload=3`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message);
      }

      // Cache current question and preloaded questions
      const { currentQuestion, preloadedQuestions, sectionInfo, examination } = result.data;

      // Update cache
      setQuestionCache(prev => {
        const newCache = new Map(prev);
        newCache.set(questionIndex, currentQuestion);

        // Cache preloaded questions
        preloadedQuestions.forEach((question, idx) => {
          newCache.set(questionIndex + idx + 1, question);
        });

        return newCache;
      });

      // Update states
      setSectionInfo(sectionInfo);
      setExamination(examination);

      return currentQuestion;

    } catch (error) {
      console.error('Error fetching question:', error);
      message.error('Failed to load question');
      return null;
    } finally {
      setLoading(false);
    }
  }, [examinationId, questionCache]);

  const fetchTimeStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/examinations/${examinationId}/time-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        const { timeRemaining, status } = result.data;

        setTimeRemaining({
          minutes: timeRemaining.minutes,
          seconds: timeRemaining.seconds,
          percentage: timeRemaining.percentage,
          totalMs: timeRemaining.totalMs,
          formatted: timeRemaining.formatted
        });

        setTimeWarning(status.warning);
        setTimeCritical(status.critical);

        // Auto-complete if time expired
        if (status.expired && examination?.status === 'in-progress') {
          // Handle time expiry - will be handled in useEffect
          console.log('Time expired, examination should complete');
        }
      }
    } catch (error) {
      console.error('Error fetching time status:', error);
    }
  }, [examinationId, examination?.status]);

  const submitAnswer = useCallback(async (questionId, answer, timeSpent = 0) => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/examinations/${examinationId}/submit-answer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questionId,
          userAnswer: answer,
          timeSpent
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message);
      }

      return result.data;
    } catch (error) {
      console.error('Error submitting answer:', error);
      message.error('Failed to save answer');
      return null;
    }
  }, [examinationId]);

  // Navigation functions
  const goToQuestion = useCallback(async (questionIndex) => {
    if (questionIndex < 0 || !examination || questionIndex >= examination.totalQuestions) {
      return;
    }

    // Save current answer if selected
    if (selectedAnswer && currentQuestion) {
      await submitAnswer(currentQuestion._id, selectedAnswer);
      setUserAnswers(prev => new Map(prev.set(currentQuestionIndex, selectedAnswer)));
    }

    // Load new question
    const question = await fetchQuestion(questionIndex);
    if (question) {
      setCurrentQuestion(question);
      setCurrentQuestionIndex(questionIndex);

      // Set previous answer if exists
      const previousAnswer = question.userAnswer?.answer || null;
      setSelectedAnswer(previousAnswer);
    }

    // Preload next questions in background
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
    }

    preloadTimeoutRef.current = setTimeout(() => {
      // Preload next few questions silently
      for (let i = 1; i <= 2; i++) {
        const nextIndex = questionIndex + i;
        if (nextIndex < examination.totalQuestions && !questionCache.has(nextIndex)) {
          fetchQuestion(nextIndex, false); // Don't use cache, force fetch
        }
      }
    }, 100);

  }, [selectedAnswer, currentQuestion, currentQuestionIndex, examination, fetchQuestion, submitAnswer, questionCache]);

  const handleNextQuestion = () => {
    if (currentQuestionIndex < examination?.totalQuestions - 1) {
      goToQuestion(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      goToQuestion(currentQuestionIndex - 1);
    }
  };

  // Answer handling
  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);

    // Auto-save answer after short delay
    if (currentQuestion) {
      setTimeout(() => {
        submitAnswer(currentQuestion._id, answer);
        setUserAnswers(prev => new Map(prev.set(currentQuestionIndex, answer)));
      }, 500);
    }
  };

  // Examination control
  const handleCompleteExamination = useCallback(async () => {
    Modal.confirm({
      title: 'Complete Examination?',
      content: 'Are you sure you want to complete this examination? You cannot make changes after completion.',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');

          const response = await fetch(`/examinations/${examinationId}/complete`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          const result = await response.json();

          if (result.success) {
            message.success('Examination completed successfully!');
            if (onComplete) {
              onComplete(result.data);
            }
          } else {
            throw new Error(result.message);
          }
        } catch (error) {
          console.error('Error completing examination:', error);
          message.error('Failed to complete examination');
        }
      }
    });
  }, [examinationId, onComplete]);

  // Start examination
  const startExamination = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/examinations/${examinationId}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message);
      }

      return result.data;
    } catch (error) {
      console.error('Error starting examination:', error);
      message.error('Failed to start examination');
      return null;
    }
  }, [examinationId]);

  // Initialize component
  useEffect(() => {
    const initializeExamination = async () => {
      // Start the examination first
      const startResult = await startExamination();
      if (!startResult) {
        return;
      }

      // Then fetch the first question
      const question = await fetchQuestion(0);
      if (question) {
        setCurrentQuestion(question);
        setCurrentQuestionIndex(0);

        // Set previous answer if exists
        const previousAnswer = question.userAnswer?.answer || null;
        setSelectedAnswer(previousAnswer);
      }
    };

    initializeExamination();

    // Start timer
    fetchTimeStatus(); // Initial fetch
    timerIntervalRef.current = setInterval(fetchTimeStatus, 1000);

    // Cleanup
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
    };
  }, [examinationId, fetchQuestion, fetchTimeStatus, startExamination]);

  // Render loading state
  if (loading && !currentQuestion) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <p>Loading examination...</p>
      </div>
    );
  }

  // Render error state
  if (!currentQuestion && !loading) {
    return (
      <div className={styles.errorContainer}>
        <h3>Unable to load examination</h3>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className={styles.examinationContainer}>
      {/* Header with timer and progress */}
      <div className={styles.header}>
        <div className={styles.examInfo}>
          <h2>{examination?.title}</h2>
          {sectionInfo.current && (
            <div className={styles.sectionInfo}>
              <span className={styles.sectionName}>{sectionInfo.current.sectionType}</span>
              <span className={styles.questionNumber}>
                Question {sectionInfo.questionNumberInSection} of {sectionInfo.current.totalQuestions}
              </span>
            </div>
          )}
        </div>

        <div className={styles.timerContainer}>
          <div className={`${styles.timer} ${timeCritical ? styles.critical : timeWarning ? styles.warning : ''}`}>
            <ClockCircleOutlined />
            <span>{timeRemaining.formatted || '0:00'}</span>
          </div>
          <div className={styles.progressBar}>
            <Progress
              percent={timeRemaining.percentage}
              showInfo={false}
              strokeColor={timeCritical ? '#ff4d4f' : timeWarning ? '#faad14' : '#1890ff'}
              size="small"
            />
          </div>
        </div>
      </div>

      {/* Question content */}
      <div className={styles.questionContainer}>
        <div className={styles.questionHeader}>
          <span className={styles.questionCounter}>
            Question {currentQuestionIndex + 1} of {examination?.totalQuestions}
          </span>
          <span className={styles.difficulty}>
            {currentQuestion?.difficulty?.charAt(0).toUpperCase() + currentQuestion?.difficulty?.slice(1)}
          </span>
        </div>

        <div className={styles.questionText}>
          {currentQuestion?.questionText}
        </div>

        {/* Media content */}
        {currentQuestion?.media && (
          <div className={styles.mediaContainer}>
            <img src={currentQuestion.media} alt="Question media" className={styles.questionImage} />
          </div>
        )}

        {/* Answer choices */}
        <div className={styles.choicesContainer}>
          {currentQuestion?.choices?.map((choice, index) => {
            const choiceKey = String.fromCharCode(65 + index); // A, B, C, D
            const isSelected = selectedAnswer === choiceKey;

            return (
              <div
                key={choiceKey}
                className={`${styles.choice} ${isSelected ? styles.selected : ''}`}
                onClick={() => handleAnswerSelect(choiceKey)}
              >
                <div className={styles.choiceLabel}>{choiceKey}</div>
                <div className={styles.choiceText}>{choice}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation controls */}
      <div className={styles.navigationContainer}>
        <div className={styles.navigationLeft}>
          <Button
            icon={<LeftOutlined />}
            onClick={handlePreviousQuestion}
            disabled={currentQuestionIndex === 0}
            size="large"
          >
            Previous
          </Button>
        </div>

        <div className={styles.navigationCenter}>
          <div className={styles.progressInfo}>
            <span>Answered: {Array.from(userAnswers.keys()).length}</span>
            <span>Remaining: {examination?.totalQuestions - Array.from(userAnswers.keys()).length}</span>
          </div>
        </div>

        <div className={styles.navigationRight}>
          {currentQuestionIndex < examination?.totalQuestions - 1 ? (
            <Button
              type="primary"
              icon={<RightOutlined />}
              iconPosition="end"
              onClick={handleNextQuestion}
              size="large"
            >
              Next
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleCompleteExamination}
              size="large"
              className={styles.completeButton}
            >
              Complete Examination
            </Button>
          )}
        </div>
      </div>

      {/* Section breakdown (SAT format) */}
      {sectionInfo.allSections.length > 1 && (
        <div className={styles.sectionBreakdown}>
          {sectionInfo.allSections.map((section, index) => (
            <div key={index} className={styles.sectionItem}>
              <div className={styles.sectionName}>{section.sectionType}</div>
              <div className={styles.sectionDetails}>
                {section.totalQuestions} questions • {section.timeLimit} min
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SATExaminationContent;